import {
  Box,
  Chip,
  Divider,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Link, LinkButton } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { entityRouteParams, entityRouteRef } from '@backstage/plugin-catalog-react';
import { ArchEdge, ArchNode, EdgeCategory } from './graph';
import { CATEGORY_COLORS } from './nodes';
import {
  EntityHealth,
  SEVERITY_COLORS,
  SEVERITY_MARKS,
  STATE_COLORS,
  STATE_LABELS,
  healthState,
  issueSource,
  issueText,
  UNKNOWN_CAUSE,
  kubernetesTabPath,
} from '../health/api';

const useStyles = makeStyles(theme => ({
  root: {
    width: 380,
    flexShrink: 0,
    height: '100%',
    overflowY: 'auto',
    borderLeft: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    padding: theme.spacing(2),
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  title: {
    flex: 1,
    fontWeight: 700,
    wordBreak: 'break-word',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    margin: theme.spacing(1, 0),
  },
  issue: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    alignItems: 'flex-start',
  },
  issueMark: { fontWeight: 700, lineHeight: '20px', width: 12, flexShrink: 0 },
  healthMeta: { marginTop: theme.spacing(1) },
  healthLinks: { display: 'flex', gap: theme.spacing(2), flexWrap: 'wrap', marginTop: theme.spacing(0.5) },
  section: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: theme.palette.text.secondary,
    marginBottom: 4,
  },
  description: {
    whiteSpace: 'pre-line',
    fontSize: 13,
  },
  relation: {
    display: 'block',
    padding: '2px 0',
    fontSize: 13,
    cursor: 'pointer',
    color: theme.palette.text.primary,
    textDecoration: 'underline',
    textDecorationColor: theme.palette.divider,
    background: 'none',
    border: 'none',
    textAlign: 'left',
    fontFamily: 'inherit',
    '&:hover': { color: theme.palette.primary.main },
  },
  field: {
    fontSize: 13,
  },
}));

// В YAML описания переносятся по ширине строки: склеиваем строки, но сохраняем абзацы (пустая строка)
// и пункты списков (строки, начинающиеся с «-», «*» или «•»).
const formatDescription = (text: string) =>
  text
    .split(/\n\s*\n/)
    .map(paragraph =>
      paragraph
        .replace(/\n\s*(?=[-*•]\s)/g, '\u0000')
        .replace(/\s*\n\s*/g, ' ')
        .split('\u0000')
        .map(line => line.trim())
        .join('\n'),
    )
    .filter(Boolean)
    .join('\n\n');

type Props = {
  node: ArchNode;
  nodesById: Map<string, ArchNode>;
  edges: ArchEdge[];
  onSelect: (id: string) => void;
  onClose: () => void;
  health?: EntityHealth;
};

// Связи узла в обе стороны: заголовок секции зависит от направления и типа связи.
const RELATION_SECTIONS: {
  title: string;
  category: EdgeCategory;
  direction: 'out' | 'in';
}[] = [
  { title: 'Зависит от', category: 'dependsOn', direction: 'out' },
  { title: 'От него зависят', category: 'dependsOn', direction: 'in' },
  { title: 'Предоставляет API', category: 'providesApi', direction: 'out' },
  { title: 'Использует API', category: 'consumesApi', direction: 'out' },
  { title: 'Кто предоставляет', category: 'providesApi', direction: 'in' },
  { title: 'Кто использует', category: 'consumesApi', direction: 'in' },
];

export const EntityPanel = ({ node, nodesById, edges, onSelect, onClose, health }: Props) => {
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);
  const { entity } = node;
  const spec = (entity.spec ?? {}) as Record<string, unknown>;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          {node.label}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Закрыть">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      <div className={classes.chips}>
        <Chip
          size="small"
          label={entity.kind}
          style={{ background: CATEGORY_COLORS[node.category], color: '#fff' }}
        />
        {typeof spec.type === 'string' && <Chip size="small" label={spec.type} />}
        {typeof spec.lifecycle === 'string' && (
          <Chip size="small" variant="outlined" label={spec.lifecycle} />
        )}
      </div>
      <LinkButton
        variant="contained"
        color="primary"
        size="small"
        endIcon={<OpenInNewIcon fontSize="small" />}
        to={entityRoute(entityRouteParams(entity))}
      >
        Открыть страницу
      </LinkButton>

      {health && (
        <Box className={classes.section}>
          <div className={classes.sectionTitle}>Состояние</div>
          <Chip
            size="small"
            label={STATE_LABELS[healthState(health)]}
            style={{ background: STATE_COLORS[healthState(health)], color: '#fff', fontWeight: 600 }}
          />
          {/* Причины — главное в блоке: что именно не так и где. */}
          {healthState(health) !== 'ok' &&
            healthState(health) !== 'stopped' &&
            !health.issues.some(i => i.severity !== 'info') && (
              <Typography variant="body2" className={classes.issue}>
                {UNKNOWN_CAUSE}
              </Typography>
            )}
          {health.issues.map((issue, i) => (
            <div key={i} className={classes.issue}>
              <span className={classes.issueMark} style={{ color: SEVERITY_COLORS[issue.severity] }}>
                {SEVERITY_MARKS[issue.severity]}
              </span>
              <span>
                <Typography variant="body2" component="span">
                  {issueText(issue)}
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div">
                  {issueSource(issue)}
                </Typography>
              </span>
            </div>
          ))}
          <Typography variant="caption" color="textSecondary" component="div" className={classes.healthMeta}>
            {[
              health.clusters.length > 0 &&
                `${health.pods.ready}/${health.pods.live} подов готовы · ${health.clusters.join(', ')}`,
              health.alerts.rules > 0 &&
                (health.alerts.firing > 0
                  ? `сработало алертов: ${health.alerts.firing} из ${health.alerts.rules}`
                  : `правил алертинга: ${health.alerts.rules}, все в норме`),
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          <div className={classes.healthLinks}>
            {health.clusters.length > 0 && (
              <Link to={kubernetesTabPath(health.entityRef)}>Вкладка Kubernetes</Link>
            )}
            <Link to={entityRoute(entityRouteParams(entity))}>Страница сервиса (логи, алерты)</Link>
          </div>
        </Box>
      )}

      {typeof spec.owner === 'string' && (
        <Box className={classes.section}>
          <div className={classes.sectionTitle}>Владелец</div>
          <Typography className={classes.field}>{spec.owner}</Typography>
        </Box>
      )}

      {entity.metadata.description && (
        <Box className={classes.section}>
          <div className={classes.sectionTitle}>Описание</div>
          <Typography className={classes.description}>
            {formatDescription(entity.metadata.description)}
          </Typography>
        </Box>
      )}

      {!!entity.metadata.links?.length && (
        <Box className={classes.section}>
          <div className={classes.sectionTitle}>Ссылки</div>
          {entity.metadata.links.map(link => (
            <Link key={link.url} to={link.url} className={classes.relation}>
              {link.title ?? link.url}
            </Link>
          ))}
        </Box>
      )}

      <Divider className={classes.section} />

      {RELATION_SECTIONS.map(section => {
        const related = edges
          .filter(
            e =>
              e.category === section.category &&
              (section.direction === 'out' ? e.source === node.id : e.target === node.id),
          )
          .map(e => nodesById.get(section.direction === 'out' ? e.target : e.source))
          .filter((n): n is ArchNode => !!n)
          .sort((a, b) => a.label.localeCompare(b.label));
        if (!related.length) return null;
        return (
          <Box className={classes.section} key={section.title}>
            <div className={classes.sectionTitle}>
              {section.title} ({related.length})
            </div>
            {related.map(r => (
              <button
                type="button"
                key={r.id}
                className={classes.relation}
                onClick={() => onSelect(r.id)}
              >
                {r.label}
              </button>
            ))}
          </Box>
        );
      })}

      {!!entity.metadata.tags?.length && (
        <Box className={classes.section}>
          <div className={classes.sectionTitle}>Теги</div>
          <div className={classes.chips}>
            {entity.metadata.tags.map(tag => (
              <Chip key={tag} size="small" variant="outlined" label={tag} />
            ))}
          </div>
        </Box>
      )}
    </div>
  );
};
